import { useState, useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { Modal, ModalTitle, ModalContent, Typography } from "@kaltura/ds-react-components";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { InlineToggleField } from "@kaltura/mediaspace-shared-ui/form";
import { NewLinksModal } from "./new-links-modal/NewLinksModal";
import { NewLinksLine } from "./new-links-line/NewLinksLine";
import { LinksTable } from "./links-table/LinksTable";
import { GuestAccessLink } from "./GuestAccessLink";
import { useGuestLinksStore } from "./GuestLinksStore";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface GuestAccessProps {
    entryId: string;
    categoryId?: string;
    enabled: boolean;
    links?: GuestAccessLink[];
    toggleUrl: string;
    saveUrl: string;
    deleteUrl: string;
}

export interface GuestAccessModalProps extends GuestAccessProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * Guest Access links management modal
 */
export const GuestAccessModal = (props: GuestAccessModalProps) => {
    const {
        entryId,
        categoryId,
        enabled: enabledProp,
        links = [],
        toggleUrl,
        saveUrl,
        deleteUrl,
        isOpen,
        onClose,
    } = props;
    const [processing, setProcessing] = useState(false);
    const [open, setOpen] = useState(false);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    // get global state
    const initData = useGuestLinksStore((state) => state.initData);
    const setEnabled = useGuestLinksStore((state) => state.setEnabled);
    const setGuestLinks = useGuestLinksStore((state) => state.setGuestLinks);
    const enabled = useGuestLinksStore((state) => state.enabled);
    const guestLinks = useGuestLinksStore((state) => state.guestLinks);

    // init state
    useEffect(() => {
        initData(enabledProp, links);
    }, [initData, enabledProp, links]);

    const handleToggleGuestAccess = async (active: boolean) => {
        setProcessing(true);
        setEnabled(active);

        try {
            const { success, message } = await postKmsData(
                toggleUrl,
                { id: entryId, categoryId: categoryId, enabled: active }
            );
            // show toast messages
            message && showToast({ severity: success ? "success" : "error", message: message });
        }
        catch (e) {
            showToast({ severity: "error", message: translate("Error while saving data") });
            setEnabled(!active);
        }

        setProcessing(false);
        sendButtonAnalytics(`Guest access link - ${active ? "Enable" : "Disable"}`, ButtonClickAnalyticsType.TOGGLE);
    };

    const handleNewLink = async (data: FieldValues) => {
        try {
            const { success, message, link } = await postKmsData(
                saveUrl,
                { id: entryId, categoryId: categoryId, data: data }
            );

            // add new link
            if (success) {
                setGuestLinks([...guestLinks, link]);
            }

            // show toast messages
            message && showToast({ severity: success ? "success" : "error", message: message });
        }
        catch (e) {
            showToast({ severity: "error", message: translate("Error while saving data") });
        }

        setOpen(false);
    };

    const handleDelete = async (ks: string) => {
        try {
            const { success, message } = await postKmsData(deleteUrl, { id: entryId, categoryId: categoryId, ks: ks });

            // remove deleted link
            if (success) {
                setGuestLinks(guestLinks.filter((link) => link.ks !== ks));
            }

            // show toast messages
            message && showToast({ severity: success ? "success" : "error", message: message });
        }
        catch (e) {
            showToast({ severity: "error", message: translate("Error while deleting link") });
        }
    };

    return (
        <Modal
            open={isOpen}
            fullWidth={true}
            // Prevent menu item keydown propagation
            onKeyDown={(e) => {
                e.stopPropagation();
            }}
            // support normal close - escape key, external click
            onClose={onClose}
        >
            <ModalTitle onClose={onClose}>
                <Typography variant={"h3"} component={"div"}>
                    {translate("Manage guest access")}
                </Typography>
            </ModalTitle>

            <ModalContent>
                <Typography variant={"body2"}>
                    {translate("Allow link-based access without sign-in. Up to 3 guest links available.")}
                </Typography>

                <InlineToggleField
                    id="enable-guest"
                    label={translate("Enable guest access")}
                    checked={!!enabled}
                    disabled={processing}
                    onChange={handleToggleGuestAccess}
                />

                <NewLinksLine linksNumber={guestLinks?.length} onClick={() => setOpen(true)} />

                <NewLinksModal open={open} onSubmit={handleNewLink} onClose={() => setOpen(false)} />

                {guestLinks.length > 0 && <LinksTable entryId={entryId} links={guestLinks} onDelete={handleDelete} />}
            </ModalContent>
        </Modal>
    );
};

export default GuestAccessModal;
